import { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { supabase } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Modal, ModalHeader, ModalTitle } from "@/components/ui/modal";

import type { CodeAuditAction, CodeAuditType } from "@/lib/codeAuditLog";

type AuditAction = CodeAuditAction;
type CodeType = CodeAuditType;

type AuditRow = {
  id: string;
  created_at: string;
  action: AuditAction;
  code_type: CodeType;
  reference_id: string | null;
  code_label: string | null;
  team_names: string;
  user_email: string;
  details: Record<string, unknown> | null;
};

function formatDateTimeWithSeconds(value: string): string {
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function csvEscape(v: unknown) {
  const s = String(v ?? "");
  const needsQuotes = /[",\n]/.test(s);
  const escaped = s.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
}

function downloadTextFile(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const ACTION_LABELS: Record<AuditAction, string> = {
  code_viewed: "Código visualizado",
  code_copied: "Código TOTP copiado",
  code_created: "Código adicionado",
  code_updated: "Código editado",
  code_deleted: "Código excluído",
  member_created: "Membro criado",
  member_updated: "Membro editado",
  member_deleted: "Membro excluído",
};

function actionBadgeClasses(action: AuditAction) {
  if (action === "code_viewed") {
    return "border-border text-foreground";
  }
  return "border-border text-foreground";
}

const DETAIL_LABELS: Record<string, string> = {
  name: "Nome",
  is_admin: "Admin",
  teams_added: "Times adicionados",
  teams_removed: "Times removidos",
  category: "Categoria",
};

// name/is_admin vêm como [antigo, novo]; times como lista
function formatDetail(k: string, v: unknown): string {
  if (!Array.isArray(v)) return String(v);
  if (k.startsWith("teams_")) return v.join(", ");
  return v.map((x) => String(x) || "—").join(" → ");
}

function detailsText(r: AuditRow): string {
  return Object.entries(r.details ?? {})
    .map(([k, v]) => `${DETAIL_LABELS[k] ?? k}: ${formatDetail(k, v)}`)
    .join("; ");
}

const ALL_ACTIONS = Object.keys(ACTION_LABELS) as AuditAction[];

function actionLabel(action: AuditAction) {
  return ACTION_LABELS[action] ?? action;
}

export function AuditLogs() {
  const [days, setDays] = useState<"all" | "30" | "60" | "90">("all");
  const [actionFilter, setActionFilter] = useState<"all" | AuditAction>("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportDays, setExportDays] = useState<"30" | "60" | "90">("30");
  const [exportActions, setExportActions] = useState<AuditAction[]>(ALL_ACTIONS);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const totalPages = useMemo(() => {
    if (!count) return 1;
    return Math.max(1, Math.ceil(count / pageSize));
  }, [count, pageSize]);

  const fetchRows = useCallback(
    async (opts?: { exportAll?: boolean; actions?: AuditAction[]; days?: string }) => {
      setErrorMsg(null);
      if (!opts?.exportAll) setLoading(true);

      try {
        if (opts?.exportAll) {
          // Export: fetch all (best-effort, in pages) respecting filters/search.
          const batchSize = 1000;
          let offset = 0;
          const all: AuditRow[] = [];

          // eslint-disable-next-line no-constant-condition
          while (true) {
            let q = supabase
              .from("code_audit_log")
              .select(
                "id, created_at, action, code_type, reference_id, code_label, team_names, user_email, details"
              )
              .order("created_at", { ascending: false })
              .range(offset, offset + batchSize - 1);

            q = q.in("action", opts.actions ?? ALL_ACTIONS);
            if (opts.days) {
              q = q.gte("created_at", new Date(Date.now() - Number(opts.days) * 864e5).toISOString());
            }

            const s = search.trim();
            if (s) {
              q = q.or(
                `user_email.ilike.%${s}%,team_names.ilike.%${s}%,code_label.ilike.%${s}%,reference_id.ilike.%${s}%`
              );
            }

            const { data, error } = await q;
            if (error) throw error;

            const batch = (data ?? []) as AuditRow[];
            all.push(...batch);
            if (batch.length < batchSize) break;
            offset += batchSize;
            if (all.length > 20000) break; // guardrail
          }

          return all;
        }

        const from = page * pageSize;
        const to = from + pageSize - 1;

        let q = supabase
          .from("code_audit_log")
          .select(
            "id, created_at, action, code_type, reference_id, code_label, team_names, user_email, details",
            { count: "exact" }
          )
          .order("created_at", { ascending: false })
          .range(from, to);

        if (actionFilter !== "all") q = q.eq("action", actionFilter);
        if (days !== "all") {
          q = q.gte("created_at", new Date(Date.now() - Number(days) * 864e5).toISOString());
        }

        const s = search.trim();
        if (s) {
          q = q.or(
            `user_email.ilike.%${s}%,team_names.ilike.%${s}%,code_label.ilike.%${s}%,reference_id.ilike.%${s}%`
          );
        }

        const { data, count: total, error } = await q;
        if (error) throw error;

        setRows(((data ?? []) as AuditRow[]) || []);
        setCount(total ?? 0);
      } catch (err: any) {
        console.error("[AuditLogs] fetch error:", err);
        setRows([]);
        setCount(0);
        setErrorMsg(
          err?.message || "Não foi possível carregar os logs de auditoria."
        );
      } finally {
        if (!opts?.exportAll) setLoading(false);
      }
    },
    [actionFilter, days, page, pageSize, search]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      fetchRows();
    }, 300);
    return () => clearTimeout(t);
  }, [fetchRows]);

  useEffect(() => {
    setPage(0);
  }, [actionFilter, days, search, pageSize]);

  const detailsTable = useCallback((r: AuditRow) => {
    const rows: { label: string; value: string }[] = [
      { label: "Tipo", value: { totp: "TOTP", email: "Email", member: "Membro" }[r.code_type] },
      { label: r.code_type === "member" ? "Membro" : "Nome da conta", value: r.code_label ?? "—" },
      { label: "ID de referência", value: r.reference_id ?? "—" },
      ...Object.entries(r.details ?? {}).map(([k, v]) => ({
        label: DETAIL_LABELS[k] ?? k,
        value: formatDetail(k, v),
      })),
    ];
    return (
      <table className="w-full min-w-[200px] text-xs border border-border rounded-md overflow-hidden">
        <tbody className="divide-y divide-border">
          {rows.map(({ label, value }) => (
            <tr key={label} className="bg-card/50">
              <td className="px-2 py-1.5 text-muted-foreground font-medium w-32">
                {label}
              </td>
              <td className="px-2 py-1.5 text-foreground break-all">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }, []);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const all = (await fetchRows({ exportAll: true, actions: exportActions, days: exportDays })) ?? [];
      const header = [
        "created_at",
        "action",
        "code_type",
        "reference_id",
        "code_label",
        "team_names",
        "user_email",
        "details",
      ];
      const lines = [
        header.join(","),
        ...all.map((r) =>
          [
            r.created_at,
            r.action,
            r.code_type,
            r.reference_id ?? "",
            r.code_label ?? "",
            r.team_names ?? "",
            r.user_email ?? "",
            detailsText(r),
          ]
            .map(csvEscape)
            .join(",")
        ),
      ];
      downloadTextFile(
        `audit_logs_${Date.now()}.csv`,
        lines.join("\n"),
        "text/csv;charset=utf-8;"
      );
      setExportOpen(false);
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const all = (await fetchRows({ exportAll: true, actions: exportActions, days: exportDays })) ?? [];
      const rows = all.map((r) => ({
        "Data/Hora": formatDateTimeWithSeconds(r.created_at),
        Ação: actionLabel(r.action),
        Recurso: r.code_type,
        reference_id: r.reference_id ?? "",
        code_label: r.code_label ?? "",
        team_names: r.team_names ?? "",
        Usuário: r.user_email ?? "",
        Detalhes: detailsText(r),
      }));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), "audit_logs");
      XLSX.writeFile(workbook, `audit_logs_${Date.now()}.xlsx`);
      setExportOpen(false);
    } finally {
      setExporting(false);
    }
  };

  const canPrev = page > 0;
  const canNext = page + 1 < totalPages;

  return (
    <div className="min-h-screen bg-background text-foreground p-4">
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">
              Logs de Auditoria
            </h1>
            <p className="text-sm text-muted-foreground">
              Visualização e cópia de códigos (TOTP e Email) — apenas admins.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              setExportActions(ALL_ACTIONS);
              setExportDays("30");
              setExportOpen(true);
            }}
            disabled={loading}
            className="border-border text-foreground hover:text-foreground hover:bg-accent"
          >
            Exportar logs
          </Button>
        </div>

        <Modal isOpen={exportOpen} onClose={() => setExportOpen(false)}>
          <ModalHeader>
            <ModalTitle>Exportar logs</ModalTitle>
          </ModalHeader>
          <div className="mt-4 space-y-2">
            <p className="text-sm text-muted-foreground">Tipos de log a exportar:</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {ALL_ACTIONS.map((a) => (
                <label key={a} className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={exportActions.includes(a)}
                    onChange={(e) =>
                      setExportActions((prev) =>
                        e.target.checked ? [...prev, a] : prev.filter((x) => x !== a)
                      )
                    }
                  />
                  {ACTION_LABELS[a]}
                </label>
              ))}
            </div>
            <label className="text-sm text-muted-foreground block pt-2">Período</label>
            <select
              value={exportDays}
              onChange={(e) => setExportDays(e.target.value as typeof exportDays)}
              className="w-full h-10 rounded-md border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="30">Últimos 30 dias</option>
              <option value="60">Últimos 60 dias</option>
              <option value="90">Últimos 90 dias</option>
            </select>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setExportOpen(false)}
              disabled={exporting}
              className="border-border text-foreground hover:text-foreground hover:bg-accent"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleExportCsv}
              disabled={exporting || exportActions.length === 0}
            >
              {exporting ? "Exportando..." : "Exportar CSV"}
            </Button>
            <Button
              onClick={handleExportExcel}
              disabled={exporting || exportActions.length === 0}
            >
              {exporting ? "Exportando..." : "Exportar XLSX"}
            </Button>
          </div>
        </Modal>

        <div className="bg-card/60 border border-border rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-muted-foreground block mb-2">
                Filtrar por Ação
              </label>
              <select
                value={actionFilter}
                onChange={(e) =>
                  setActionFilter(e.target.value as "all" | AuditAction)
                }
                className="w-full h-10 rounded-md border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="all">Todas as ações</option>
                <option value="code_viewed">{ACTION_LABELS.code_viewed}</option>
                {(Object.keys(ACTION_LABELS) as AuditAction[])
                  .filter((a) => a !== "code_viewed")
                  .map((a) => (
                    <option key={a} value={a}>
                      {ACTION_LABELS[a]}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground block mb-2">Período</label>
              <select
                value={days}
                onChange={(e) => setDays(e.target.value as typeof days)}
                className="w-full h-10 rounded-md border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="all">Todo o período</option>
                <option value="30">Últimos 30 dias</option>
                <option value="60">Últimos 60 dias</option>
                <option value="90">Últimos 90 dias</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground block mb-2">Buscar</label>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar em logs..."
                className="bg-card border-border text-foreground"
              />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {errorMsg && (
            <div className="p-4 text-sm text-destructive border-b border-border">
              {errorMsg}
            </div>
          )}

          <div className="w-full overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-background/30 text-muted-foreground">
                <tr className="[&_th]:px-4 [&_th]:py-3 [&_th]:text-left border-b border-border">
                  <th>Data/Hora</th>
                  <th>Ação</th>
                  <th>Recurso</th>
                  <th>Detalhes</th>
                  <th>Times</th>
                  <th>Usuário</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr>
                    <td className="px-4 py-6 text-muted-foreground" colSpan={6}>
                      Carregando...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-muted-foreground" colSpan={6}>
                      Nenhum log encontrado.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr
                      key={r.id}
                      className="hover:bg-accent/40 transition-colors"
                    >
                      <td className="px-4 py-4 text-foreground whitespace-nowrap">
                        {formatDateTimeWithSeconds(r.created_at)}
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${actionBadgeClasses(
                            r.action
                          )}`}
                        >
                          {actionLabel(r.action)}
                        </span>
                      </td>
                      <td className="px-4 py-4 font-medium text-foreground">
                        {r.code_type}
                      </td>
                      <td className="px-4 py-4 text-muted-foreground">
                        {detailsTable(r)}
                      </td>
                      <td className="px-4 py-4 text-foreground">
                        {r.team_names || "—"}
                      </td>
                      <td className="px-4 py-4 text-foreground">
                        {r.user_email}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-card">
            <div className="text-xs text-muted-foreground">
              Página {page + 1} de {totalPages} • {count} registros
            </div>
            <div className="flex items-center gap-2">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="h-9 rounded-md border border-border bg-card px-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <Button
                variant="outline"
                disabled={!canPrev || loading}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="border-border text-foreground hover:text-foreground hover:bg-accent"
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                disabled={!canNext || loading}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                className="border-border text-foreground hover:text-foreground hover:bg-accent"
              >
                Próxima
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

